import React from 'react';
import { Grid, TextField, InputAdornment, Button } from '@mui/material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { useDebounce } from 'use-debounce';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { UserUtils } from 'utils/user-utils';
import { UserPermissions } from 'interfaces/user/Permissions';
import { ISignatureRequirement } from 'models/signature-requirement/types';
import CustomerDropdown from '../components/customer-dropdown';
import { SectionTitleStyled } from '../components/section-title';
import { SignatureRequirementTable } from './signature-requirement-table';
import { TriggerTypeFilter } from './components/trigger-type-filter';
import { AddSignatureRequirementModal } from './modals/add-signature-requirement-modal';
import { DeleteConfirmationModal } from './modals/delete-confirmation-modal';
import { SignatureRequirementStyles } from './index.styles';
import {
  searchFieldPlaceholder,
  debounceTimeMs,
  triggerTypeDebounceTimeMs,
  initialSearchTerm,
} from './constants';

type ISignatureRequirementProps = WithStyles<typeof SignatureRequirementStyles>;

const SignatureRequirement = (props: ISignatureRequirementProps): JSX.Element => {
  const { classes } = props;

  // State
  const [searchTerm, setSearchTerm] = React.useState<string>(initialSearchTerm);
  const [debouncedSearchTerm] = useDebounce<string | undefined>(searchTerm, debounceTimeMs);
  const [selectedTriggerTypes, setSelectedTriggerTypes] = React.useState<string[]>(['all']);
  // Debounce trigger type changes so rapid multi-select clicks don't spam the API.
  const [debouncedTriggerTypes] = useDebounce<string[]>(
    selectedTriggerTypes,
    triggerTypeDebounceTimeMs,
  );
  const [addModalOpen, setAddModalOpen] = React.useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState<boolean>(false);
  // Checked rule IDs lifted from table so Exclude button can be enabled/disabled here
  const [checkedRuleIds, setCheckedRuleIds] = React.useState<number[]>([]);
  const hasCheckedItems = checkedRuleIds.length > 0;
  const [reloadDataTrigger, setReloadDataTrigger] = React.useState<Date | undefined>(undefined);
  const [allRules, setAllRules] = React.useState<ISignatureRequirement[]>([]);

  // Redux state
  const stateSelectedCustomerId = useTypedSelector(state => state.filters.selectedCustomerId);
  const customers = useTypedSelector(state => state.filters.customers);
  const user = useTypedSelector(state => state.auth.currentUser);
  const [selectedCustomerId, setSelectedCustomerId] =
    React.useState<number>(stateSelectedCustomerId);

  const onLoadData = React.useCallback((data: ISignatureRequirement[]) => {
    setAllRules(data);
  }, []);

  // Permissions
  const userHasPermission = React.useMemo<boolean>(() => {
    return UserUtils.userIsPermitted(
      UserPermissions.SignatureRequirementManage,
      selectedCustomerId,
    );
  }, [selectedCustomerId]);

  // Effects
  React.useEffect(() => {
    if (selectedCustomerId == null) {
      if (stateSelectedCustomerId != null) {
        setSelectedCustomerId(stateSelectedCustomerId);
      } else if (customers.length > 0) {
        setSelectedCustomerId(customers[0].id);
      }
    }
  }, [stateSelectedCustomerId, customers, selectedCustomerId]);

  return (
    <>
      <SectionTitleStyled title="Signature Requirement" />
      <Grid container direction="column" spacing={2}>
        {/* Filters Row */}
        <Grid item>
          <Grid container justifyContent="space-between">
            {/* Left Side - Filters */}
            <Grid item xs="auto">
              <Grid container spacing={1}>
                <Grid item xs="auto">
                  <CustomerDropdown
                    isLabelOn
                    selectedCustomerId={selectedCustomerId}
                    setSelectedCustomerId={customerId => setSelectedCustomerId(customerId)}
                  />
                </Grid>
                <Grid item xs="auto">
                  <TriggerTypeFilter
                    selectedTriggerTypes={selectedTriggerTypes}
                    setSelectedTriggerTypes={setSelectedTriggerTypes}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Right Side - Search Field + Action Buttons */}
            <Grid item xs="auto" className={classes.rightSideContainer}>
              <TextField
                variant="standard"
                placeholder={searchFieldPlaceholder}
                onChange={event => setSearchTerm(event.target.value)}
                value={searchTerm}
                className={classes.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon className={classes.searchIcon} />
                    </InputAdornment>
                  ),
                  endAdornment:
                    searchTerm != null && searchTerm !== initialSearchTerm ? (
                      <InputAdornment
                        position="end"
                        onClick={() => setSearchTerm(initialSearchTerm)}
                        className={classes.textFieldEndAdornment}
                      >
                        <CloseIcon fontSize="small" />
                      </InputAdornment>
                    ) : null,
                }}
              />
              {userHasPermission ? (
                <>
                  <Button
                    variant="outlined"
                    className={classes.modalButton}
                    onClick={() => setDeleteModalOpen(true)}
                    disabled={!hasCheckedItems}
                  >
                    Exclude
                  </Button>
                  <Button
                    variant="contained"
                    className={classes.modalButton}
                    onClick={() => setAddModalOpen(true)}
                  >
                    Add
                  </Button>
                </>
              ) : null}
            </Grid>
          </Grid>
        </Grid>

        {/* Table */}
        <Grid item>
          <SignatureRequirementTable
            searchTerm={debouncedSearchTerm}
            selectedCustomerId={selectedCustomerId}
            debouncedTriggerTypes={debouncedTriggerTypes}
            userHasPermission={userHasPermission}
            setCheckedRuleIds={setCheckedRuleIds}
            onDataLoaded={onLoadData}
            reloadDataTrigger={reloadDataTrigger}
          />
        </Grid>
      </Grid>

      {/* Add Modal */}
      <AddSignatureRequirementModal
        open={addModalOpen}
        selectedCustomerId={selectedCustomerId}
        initialValues={{
          name: '',
          site: (() => {
            const customer = customers.find(c => c.id === selectedCustomerId);
            return customer
              ? { label: customer.name, value: selectedCustomerId }
              : { label: '', value: 0 };
          })(),
          trigger: '',
          insurance_bin: '',
          insurance_pcn: '',
          insurance_group: '',
        }}
        onCancel={() => setAddModalOpen(false)}
        onSuccess={() => {
          setReloadDataTrigger(new Date());
          setAddModalOpen(false);
        }}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteModalOpen}
        ruleIdsToDelete={checkedRuleIds}
        allRules={allRules}
        onCancel={() => setDeleteModalOpen(false)}
        onSuccess={() => {
          setReloadDataTrigger(new Date());
          setCheckedRuleIds([]);
          setDeleteModalOpen(false);
        }}
      />
    </>
  );
};

export default withStyles(SignatureRequirementStyles)(SignatureRequirement);
